import { supabase } from '../supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Get the current auth token from Supabase session.
 */
async function getAuthToken() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
}

/**
 * Send a no-cors ping to /health to wake up Render's sleeping server.
 */
async function wakeUpServer() {
  try {
    await fetch(`${API_URL}/health`, { mode: 'no-cors' });
  } catch (_) {}
}

/**
 * Make an authenticated API request with cold-start retry.
 */
export async function apiFetch(path, options = {}, _attempt = 0) {
  const token = await getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  let response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      ...options,
      headers,
    });
  } catch (err) {
    if (_attempt < 4) {
      await wakeUpServer();
      await new Promise(r => setTimeout(r, _attempt === 0 ? 5000 : 3000 + _attempt * 3000));
      return apiFetch(path, options, _attempt + 1);
    }
    throw err;
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `API error: ${response.status}`);
  }

  return response.json();
}

// ============================================
// Explore / City Search
// ============================================

export async function exploreCitySearch(city, query) {
  const { suggestions } = await apiFetch('/api/ai/explore-city', {
    method: 'POST',
    body: JSON.stringify({ city, query }),
  });
  return suggestions || [];
}

export async function generateHotels(itineraryId) {
  return apiFetch(`/api/ai/generate-hotels/${itineraryId}`, { method: 'POST' });
}

// ============================================
// Atlas Files API
// ============================================

/**
 * List published atlas files (public).
 * Falls back to Supabase direct query if backend is unavailable.
 */
export async function listPublishedAtlasFiles(options = {}) {
  const limit = options.limit || 50;
  const baseCols = 'id, title, description, destination, trip_length, cover_image_url, author, source_type, published_at, moderation_status, created_at, updated_at, traveler_profiles, continent, country, view_count, like_count';

  const runQuery = (cols) => supabase
    .from('atlas_files')
    .select(cols)
    // Query Supabase directly — avoids Render cold-start latency for public data
    .not('published_at', 'is', null)
    .eq('moderation_status', 'approved')
    .order('published_at', { ascending: false })
    .limit(limit);

  // Prefer the ranking column; if the saves migration hasn't been applied yet
  // (column missing → PG code 42703), fall back gracefully to saved_count = 0.
  let { data, error } = await runQuery(`${baseCols}, saved_count`);
  if (error && (error.code === '42703' || /saved_count/.test(error.message || ''))) {
    ({ data, error } = await runQuery(baseCols));
    if (!error) data = (data || []).map((f) => ({ ...f, saved_count: 0 }));
  }
  if (error) throw error;
  return data || [];
}

/**
 * List current user's atlas files.
 * Falls back to Supabase direct query if backend is unavailable.
 */
export async function listMyAtlasFiles() {
  try {
    const { data } = await apiFetch('/api/atlas/mine');
    return data;
  } catch (_) {
    // Fallback: query Supabase directly when backend is down
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];
    const { data } = await supabase
      .from('atlas_files')
      .select('id, title, description, destination, trip_length, cover_image_url, author, source_type, published_at, moderation_status, created_at, updated_at')
      .eq('author_id', user.id)
      .order('updated_at', { ascending: false });
    return data || [];
  }
}

/**
 * Get single atlas file with active version content.
 */
export async function getAtlasFile(id) {
  const { data } = await apiFetch(`/api/atlas/${id}`);
  return data;
}

/**
 * Create a new atlas file.
 */
export async function createAtlasFile(input) {
  const { data } = await apiFetch('/api/atlas', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return data;
}

/**
 * Update atlas file metadata.
 */
export async function updateAtlasFile(id, input) {
  const { data } = await apiFetch(`/api/atlas/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
  return data;
}

/**
 * Delete an atlas file.
 */
export async function deleteAtlasFile(id) {
  return apiFetch(`/api/atlas/${id}`, { method: 'DELETE' });
}

// ============================================
// Versions API
// ============================================

/**
 * List versions for an atlas file.
 */
export async function listVersions(atlasFileId) {
  const { data } = await apiFetch(`/api/atlas/${atlasFileId}/versions`);
  return data;
}

/**
 * Create a new draft version.
 */
export async function createVersion(atlasFileId) {
  const { data } = await apiFetch(`/api/atlas/${atlasFileId}/versions`, {
    method: 'POST',
  });
  return data;
}

/**
 * Update version content (days, activities, intro, tips).
 */
export async function updateVersion(atlasFileId, versionId, content) {
  const { data } = await apiFetch(`/api/atlas/${atlasFileId}/versions/${versionId}`, {
    method: 'PUT',
    body: JSON.stringify(content),
  });
  return data;
}

/**
 * Publish a draft version.
 */
export async function publishVersion(atlasFileId, versionId) {
  const { data } = await apiFetch(`/api/atlas/${atlasFileId}/versions/${versionId}/publish`, {
    method: 'POST',
  });
  return data;
}

// ============================================
// Forking API
// ============================================

/**
 * Fork an atlas file into user's itineraries.
 * Returns { itinerary_id, forked_from }.
 */
export async function forkAtlasFile(atlasFileId) {
  const { data } = await apiFetch(`/api/atlas/${atlasFileId}/fork`, {
    method: 'POST',
  });
  return data;
}

// ============================================
// Saves API ("save to profile")
// ============================================
// Saves go straight through Supabase (RLS-protected), so they work even when
// the Render backend is cold. A trigger keeps atlas_files.saved_count in sync.

/**
 * Return the set of atlas_file ids the current user has saved.
 * Returns an empty array when signed out.
 */
export async function listMySavedIds() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from('atlas_saves')
    .select('atlas_file_id')
    .eq('user_id', user.id);
  if (error) throw error;
  return (data || []).map((r) => r.atlas_file_id);
}

/**
 * Full atlas_file records the current user has saved (for the profile "Saved" tab).
 */
export async function listMySavedFiles() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from('atlas_saves')
    .select('created_at, atlas_files(id, title, description, destination, trip_length, cover_image_url, author, source_type, published_at, moderation_status, traveler_profiles, continent, country, view_count, like_count, saved_count)')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map((r) => r.atlas_files).filter(Boolean);
}

/**
 * Save an atlas file to the current user's profile. Idempotent.
 */
export async function saveAtlasFile(atlasFileId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Sign in to save itineraries.');
  const { error } = await supabase
    .from('atlas_saves')
    .upsert({ user_id: user.id, atlas_file_id: atlasFileId }, { onConflict: 'user_id,atlas_file_id', ignoreDuplicates: true });
  if (error) throw error;
}

/**
 * Remove a saved atlas file from the current user's profile.
 */
export async function unsaveAtlasFile(atlasFileId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  const { error } = await supabase
    .from('atlas_saves')
    .delete()
    .eq('user_id', user.id)
    .eq('atlas_file_id', atlasFileId);
  if (error) throw error;
}
